import { TrashIcon } from '@heroicons/react/24/outline';
import type { NodeProps } from '@xyflow/react';
import { EdgeLabelRenderer, Handle, Position } from '@xyflow/react';
import { Crown } from 'lucide-react';
import { MouseEvent, useMemo, useState } from 'react';

import { useTheme } from '@/app/ThemeContext';
import { Tooltip } from '@/components/Tooltip';
import { useQueryDataOptional } from '@/sections/insights/queryBuilder/state/useQueryData';
import type { FlatGraphNode } from '@/sections/insights/queryBuilder/types/FlatGraph';
import { getIconOrDefault } from '@/sections/insights/queryBuilder/utils/graphIcons';

import { MODAL_CONFIG } from './modalConfig';
import { NodeDetailsModal } from './NodeDetailsModal';

/**
 * Type definition for FlowNode data prop
 * Hydrated by hydrateReactFlowElements in reactFlowTransformations.ts
 */
interface FlowNodeData {
  labels?: string[];
  filters?: unknown[];
  nodeData?: FlatGraphNode['data'];
  editable?: boolean;
  isExpanded?: boolean;
  onToggleModal?: () => void;
  isRootNode?: boolean;
}

/**
 * FlowNode - Circle with icon representing a query node
 *
 * Displays entity-specific icon based on labels
 * Falls back to default icon for unrecognized types
 * Renders inline modal when expanded
 *
 * Modal expansion state received via data props from useGraphTransformation
 */

export function FlowNode({
  id,
  data,
  positionAbsoluteX,
  positionAbsoluteY,
}: NodeProps) {
  const { currentTheme } = useTheme();
  const context = useQueryDataOptional();

  // Extract props from node data (provided by hydrateReactFlowElements)
  const nodeData = data as FlowNodeData;
  const isExpanded = nodeData?.isExpanded ?? false;
  const onToggleModal = nodeData?.onToggleModal;
  const editable = nodeData?.editable ?? true;
  const isRootNode = nodeData?.isRootNode ?? false;
  const flatNodeData = nodeData?.nodeData;

  // Hover state for visual feedback
  const [isHovered, setIsHovered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent ReactFlow pan
    onToggleModal?.();
  };

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent node selection
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (context) {
      context.dispatch({ type: 'REMOVE_NODE', nodeId: id });
    }
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  // Icon selection logic
  const iconUrl = useMemo(() => {
    const rawLabels = data?.labels;
    const labels = Array.isArray(rawLabels) ? rawLabels : [];

    // Try each label in order
    for (const label of labels) {
      const { icon, isDefault } = getIconOrDefault(
        label.toLowerCase(),
        currentTheme
      );
      if (!isDefault) {
        return icon; // Found specific icon
      }
    }

    // Fallback: use default icon
    const { icon } = getIconOrDefault(undefined, currentTheme);
    return icon;
  }, [data?.labels, currentTheme]);

  return (
    <>
      {/* Delete button - positioned above node */}
      {editable && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(${positionAbsoluteX + 24}px, ${positionAbsoluteY - 28}px)`,
              zIndex: showDeleteConfirm ? 9999 : 999,
              pointerEvents: 'all',
            }}
          >
            {!showDeleteConfirm ? (
              <button
                onClick={handleDeleteClick}
                aria-label="Delete node"
                data-testid={`delete-node-btn-${id}`}
                className="flex cursor-pointer items-center justify-center rounded-full bg-red-500 shadow-lg transition-colors hover:bg-red-600"
                style={{ width: '24px', height: '24px' }}
              >
                <TrashIcon className="h-4 w-4 text-white" aria-hidden="true" />
              </button>
            ) : (
              <div
                className="bg-chariot-background-primary border-chariot-stroke flex flex-col gap-2 rounded p-2 shadow-lg"
                style={{ minWidth: '160px' }}
                onClick={e => e.stopPropagation()}
              >
                <span className="text-chariot-text-secondary text-xs">
                  Delete this node?
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleDeleteConfirm}
                    data-testid={`confirm-delete-node-${id}`}
                    className="flex-1 rounded bg-red-500 px-2 py-1 text-xs text-white transition-colors hover:bg-red-600"
                  >
                    Yes
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    data-testid={`cancel-delete-node-${id}`}
                    className="bg-chariot-background-tertiary text-chariot-text-primary hover:bg-chariot-background-quaternary flex-1 rounded px-2 py-1 text-xs transition-colors"
                  >
                    No
                  </button>
                </div>
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Root node marker - positioned at 10:30 position (top left) */}
      {isRootNode && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(${positionAbsoluteX - 4}px, ${positionAbsoluteY - 28}px)`,
              zIndex: 999,
              pointerEvents: 'all',
            }}
          >
            <Tooltip title="Root node" placement="top">
              <button
                aria-label="Root node"
                data-testid={`root-node-marker-${id}`}
                className="flex cursor-default items-center justify-center rounded-full bg-orange-500 shadow-lg"
                style={{ width: '24px', height: '24px' }}
                onClick={e => e.stopPropagation()}
              >
                <Crown className="h-4 w-4 text-white" aria-hidden="true" />
              </button>
            </Tooltip>
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Node circle - ALWAYS rendered */}
      <div
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="bg-chariot-background-primary flex size-[72px] cursor-pointer items-center justify-center rounded-full border-4 transition-colors"
        style={{
          borderColor: isHovered
            ? 'rgb(var(--chariot-brand-primary))'
            : 'rgb(var(--chariot-stroke))',
        }}
        data-testid={`flow-node-${id}`}
      >
        {iconUrl && (
          <img
            src={iconUrl}
            alt="Node icon"
            className="size-[56px] rounded-full"
          />
        )}
      </div>

      {/* Connection handles - visible sockets for edge creation */}
      <Handle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        className="!bg-chariot-brand-primary !h-3 !w-3 !rounded-full !border-2 !border-white transition-all hover:!h-4 hover:!w-4"
        data-testid={`node-${id}-socket-left`}
      />
      <Handle
        type="source"
        position={Position.Right}
        id={`${id}-source`}
        className="!bg-chariot-brand-primary !h-3 !w-3 !rounded-full !border-2 !border-white transition-all hover:!h-4 hover:!w-4"
        data-testid={`node-${id}-socket-right`}
      />

      {/* Modal - rendered in separate layer via EdgeLabelRenderer (like EdgeDetailsModal) */}
      {isExpanded && flatNodeData && (
        // Despite its name, you can use EdgeLabelRenderer to render nodes in ReactFlow.
        // We use EdgeLabelRenderer to obtain full control of the NodeDetailsModal's styling.
        // Without this, ReactFlow chooses where and how to render NodeDetailsModal, which is not always where/how we want.
        <EdgeLabelRenderer>
          <div
            className="pointer-events-auto"
            style={{
              position: 'absolute',
              // Calculate absolute position based on node's screen coordinates
              // Center modal horizontally under node, offset vertically by node height + spacing
              transform: `translate(-38%, 0) translate(${positionAbsoluteX}px, ${positionAbsoluteY + MODAL_CONFIG.nodeRadius * 2 + MODAL_CONFIG.offset}px)`,

              // Fixed dimensions from config
              width: `${MODAL_CONFIG.width}px`,
              height: `${MODAL_CONFIG.height}px`,

              // High z-index to appear above all nodes (matching EdgeDetailsModal)
              zIndex: 1000,
            }}
            onClick={e => e.stopPropagation()} // Prevent node drag
          >
            <NodeDetailsModal
              key={id}
              nodeId={id}
              nodeDetails={flatNodeData}
              isExpanded={true}
              editable={editable && !!context} // Only editable if context available
              onClose={() => onToggleModal?.()}
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
